import React, { useState, useEffect, useCallback } from 'react';
import './TextToSpeech.css';

const TextToSpeech = ({ 
  text, 
  position = 'bottom-right', 
  size = 'medium',
  color = 'primary',
  autoStop = true,
  rate = 1,
  pitch = 1,
  volume = 1,
  autoPlay = false,
  language = 'auto' // 'auto', 'es', 'en'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const [textChunks, setTextChunks] = useState([]);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);

  // Función para dividir texto largo en chunks manejables
  const splitTextIntoChunks = useCallback((text, maxLength = 200) => {
    if (!text) return [];
    
    const sentences = text.match(/[^\.!?]+[\.!?]+/g) || [text];
    const chunks = [];
    let currentChunk = '';
    
    sentences.forEach(sentence => {
      if (currentChunk.length + sentence.length > maxLength && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk += sentence;
      }
    });
    
    if (currentChunk.trim()) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks;
  }, []);

  useEffect(() => {
    // Verificar soporte del navegador
    setIsSupported('speechSynthesis' in window);
    
    // Limpiar al desmontar el componente
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (!text || !isSupported) return;
    
    // Detener cualquier reproducción anterior cuando cambie el texto
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      setIsPaused(false);
    }
    
    // Dividir el texto en chunks manejables
    const chunks = splitTextIntoChunks(text);
    setTextChunks(chunks);
    setCurrentChunkIndex(0);
  }, [text, isSupported, splitTextIntoChunks]);

  // Función para crear y configurar un utterance
  const createUtterance = useCallback((textChunk) => {
    const utterance = new SpeechSynthesisUtterance(textChunk);
    
    // Detectar idioma automáticamente si no se especifica
    let detectedLang = language;
    if (language === 'auto') {
      // Detección simple basada en palabras comunes
      const englishWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'question', 'option', 'category'];
      const spanishWords = ['el', 'la', 'los', 'las', 'y', 'o', 'pero', 'en', 'de', 'del', 'con', 'por', 'para', 'que', 'pregunta', 'capítulo'];
      
      const lowerText = textChunk.toLowerCase();
      const englishCount = englishWords.filter(word => lowerText.includes(word)).length;
      const spanishCount = spanishWords.filter(word => lowerText.includes(word)).length;
      
      detectedLang = englishCount > spanishCount ? 'en' : 'es';
    }
    
    // Configurar parámetros de voz
    utterance.lang = detectedLang === 'en' ? 'en-US' : 'es-ES';
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    // Configurar voz apropiada
    const setAppropriateVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      let targetVoice;
      
      if (detectedLang === 'en') {
        targetVoice = voices.find(voice => 
          voice.lang.includes('en') || voice.lang.includes('EN')
        ) || voices.find(voice => 
          voice.name.toLowerCase().includes('english')
        );
      } else {
        targetVoice = voices.find(voice => 
          voice.lang.includes('es') || voice.lang.includes('ES')
        ) || voices.find(voice => 
          voice.name.toLowerCase().includes('spanish') || 
          voice.name.toLowerCase().includes('español')
        );
      }
      
      if (targetVoice) {
        utterance.voice = targetVoice;
      }
    };

    // Establecer voz apropiada si están disponibles
    if (window.speechSynthesis.getVoices().length > 0) {
      setAppropriateVoice();
    } else {
      window.speechSynthesis.onvoiceschanged = setAppropriateVoice;
    }

    return utterance;
  }, [rate, pitch, volume, language]);

  // Función para reproducir el siguiente chunk
  const playNextChunk = useCallback((chunkIndex = currentChunkIndex) => {
    if (chunkIndex >= textChunks.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentChunkIndex(0);
      return;
    }

    const utterance = createUtterance(textChunks[chunkIndex]);
    
    utterance.onstart = () => {
      setIsPlaying(true);
      setIsPaused(false);
      console.log(`Reproduciendo chunk ${chunkIndex + 1} de ${textChunks.length}`);
    };

    utterance.onend = () => {
      const nextIndex = chunkIndex + 1;
      setCurrentChunkIndex(nextIndex);
      
      if (nextIndex < textChunks.length) {
        // Reproducir siguiente chunk después de una breve pausa
        setTimeout(() => playNextChunk(nextIndex), 500);
      } else {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentChunkIndex(0);
      }
    };

    utterance.onerror = (event) => {
      console.error('Error en texto a voz:', event);
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentChunkIndex(0);
    };

    utterance.onpause = () => {
      console.log('Utterance pausado');
      setIsPaused(true);
    };

    utterance.onresume = () => {
      console.log('Utterance reanudado');
      setIsPaused(false);
    };

    setCurrentUtterance(utterance);
    window.speechSynthesis.speak(utterance);
  }, [textChunks, createUtterance]);

  const handlePlay = () => {
    if (!isSupported || textChunks.length === 0) {
      console.log('No hay soporte o no hay texto para reproducir');
      return;
    }

    if (isPaused) {
      console.log('Reanudando reproducción...');
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      // Detener cualquier reproducción anterior si autoStop está habilitado
      if (autoStop && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
      
      console.log('Iniciando reproducción de historia...');
      console.log(`Total de chunks: ${textChunks.length}`);
      setCurrentChunkIndex(0);
      playNextChunk(0);
    }
  };

  const handlePause = () => {
    if (window.speechSynthesis.speaking && !isPaused) {
      console.log('Pausando reproducción...');
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentChunkIndex(0);
  };

  // Manejar autoPlay
  useEffect(() => {
    if (autoPlay && textChunks.length > 0 && !isPlaying && !isPaused) {
      console.log('AutoPlay activado, iniciando reproducción');
      // Pequeño delay para asegurar que el componente esté montado
      setTimeout(() => {
        handlePlay();
      }, 100);
    }
  }, [autoPlay, textChunks.length, isPlaying, isPaused]);

  if (!isSupported) {
    return null; // No mostrar nada si no hay soporte
  }

  const getPositionClass = () => {
    switch (position) {
      case 'top-left': return 'tts-top-left';
      case 'top-right': return 'tts-top-right';
      case 'bottom-left': return 'tts-bottom-left';
      case 'bottom-right': return 'tts-bottom-right';
      case 'center': return 'tts-center';
      default: return 'tts-bottom-right';
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'tts-small';
      case 'medium': return 'tts-medium';
      case 'large': return 'tts-large';
      default: return 'tts-medium';
    }
  };

  const getColorClass = () => {
    switch (color) {
      case 'primary': return 'tts-primary';
      case 'secondary': return 'tts-secondary';
      case 'success': return 'tts-success';
      case 'warning': return 'tts-warning';
      case 'danger': return 'tts-danger';
      default: return 'tts-primary';
    }
  };

  return (
    <div className={`tts-container ${getPositionClass()}`}>
      <div className={`tts-controls ${getSizeClass()} ${getColorClass()}`}>
        {!isPlaying ? (
          <button 
            onClick={handlePlay}
            className="tts-button tts-play"
            title="Reproducir en voz alta"
            aria-label="Reproducir texto en voz alta"
          >
            <span className="tts-icon">▶</span>
          </button>
        ) : (
          <div className="tts-playing-controls">
            {!isPaused ? (
              <button 
                onClick={handlePause}
                className="tts-button tts-pause"
                title="Pausar"
                aria-label="Pausar reproducción"
              >
                <span className="tts-icon">⏸</span>
              </button>
            ) : (
              <button 
                onClick={handlePlay}
                className="tts-button tts-resume"
                title="Continuar"
                aria-label="Continuar reproducción"
              >
                <span className="tts-icon">▶</span>
              </button>
            )}
            
            <button 
              onClick={handleStop}
              className="tts-button tts-stop"
              title="Detener"
              aria-label="Detener reproducción"
            >
              <span className="tts-icon">⏹</span>
            </button>
          </div>
        )}
        
        {isPlaying && (
          <div className="tts-indicator">
            <div className="tts-wave">
              <span></span>
              <span></span>
              <span></span>
            </div>
            {textChunks.length > 1 && (
              <div className="tts-progress">
                <span className="tts-progress-text">
                  {currentChunkIndex + 1}/{textChunks.length}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextToSpeech;
